import _ from 'lodash';
import { isSectionGroupRepeatable, isFieldGroupRepeatable, isOptionField, isRepeatableOptionsWithLimits, } from '../structure';
import { assertUnreachable } from '../../condition-engine/helpers/assertUnreachable';
export var RepetitionIntervalBoundary;
(function (RepetitionIntervalBoundary) {
    RepetitionIntervalBoundary["minRepetitions"] = "minRepetitions";
    RepetitionIntervalBoundary["maxRepetitions"] = "maxRepetitions";
})(RepetitionIntervalBoundary || (RepetitionIntervalBoundary = {}));
export class ExpandedContextVisitor {
    repetitionIntervalBoundary;
    answerPathContext = [];
    constructor(repetitionIntervalBoundary) {
        this.repetitionIntervalBoundary = repetitionIntervalBoundary;
    }
    visitQuestionnaire(questionnaire) {
        if (!questionnaire?.length)
            return;
        for (const sectionGroup of questionnaire) {
            this.visitSectionGroup(sectionGroup);
        }
    }
    visitSectionGroup(sectionGroup) {
        if (!sectionGroup.sections?.length)
            return;
        if (isSectionGroupRepeatable(sectionGroup) && isRepeatableOptionsWithLimits(sectionGroup.options)) {
            const numberOfRepetitions = this.numberOfRepetitions(sectionGroup);
            _.times(numberOfRepetitions, (repetitionIndex) => {
                this.withAnswerPathContextFor(sectionGroup.blueprintId, sectionGroup.nodeId, repetitionIndex, () => {
                    this.visitRepeatedSectionGroup(sectionGroup);
                });
            });
        }
        else {
            for (const section of sectionGroup.sections) {
                this.visitSection(section);
            }
        }
    }
    visitRepeatedSectionGroup(sectionGroup) {
        for (const section of sectionGroup.sections) {
            this.visitSection(section);
        }
    }
    visitSection(section) {
        if (!section.subsections?.length)
            return;
        for (const subsection of section.subsections) {
            this.visitSubsection(subsection);
        }
    }
    visitSubsection(subsection) {
        if (!subsection.fieldGroups?.length)
            return;
        for (const fieldGroup of subsection.fieldGroups) {
            this.visitFieldGroup(fieldGroup);
        }
    }
    visitFieldGroup(fieldGroup) {
        if (!fieldGroup.fields?.length)
            return;
        if (isFieldGroupRepeatable(fieldGroup)) {
            const numberOfRepetitions = this.numberOfRepetitions(fieldGroup);
            _.times(numberOfRepetitions, (repetitionIndex) => {
                this.withAnswerPathContextFor(fieldGroup.blueprintId, fieldGroup.nodeId, repetitionIndex, () => {
                    this.visitRepeatedFieldGroup(fieldGroup, repetitionIndex);
                });
            });
        }
        else {
            for (const field of fieldGroup.fields) {
                this.visitField(field);
            }
        }
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    visitRepeatedFieldGroup(fieldGroup, repetitionIndex) {
        for (const field of fieldGroup.fields) {
            this.visitField(field);
        }
    }
    visitField(field) {
        if (isOptionField(field)) {
            for (const option of field.options) {
                this.visitOption(option);
            }
        }
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    visitOption(option) { }
    answerPathContextFor(blueprintId, nodeId, repetitionIndex) {
        const activeContext = this.activeContext();
        let byBlueprintId = activeContext ? activeContext.byBlueprintId : {};
        if (typeof repetitionIndex !== 'undefined') {
            byBlueprintId = {
                ...byBlueprintId,
                [blueprintId]: repetitionIndex,
            };
        }
        let byNodeId = activeContext ? activeContext.byNodeId : {};
        if (typeof repetitionIndex !== 'undefined') {
            byNodeId = {
                ...byNodeId,
                [nodeId]: repetitionIndex,
            };
        }
        return {
            byBlueprintId,
            byNodeId,
        };
    }
    withAnswerPathContextFor(blueprintId, nodeId, repetitionIndex, callback) {
        this.enterAnswerPathContext(blueprintId, nodeId, repetitionIndex);
        callback();
        this.exitAnswerPathContext();
    }
    enterAnswerPathContext(blueprintId, nodeId, repetitionIndex) {
        this.answerPathContext.push(this.answerPathContextFor(blueprintId, nodeId, repetitionIndex));
    }
    exitAnswerPathContext() {
        if (this.answerPathContext.length === 0) {
            throw new Error('Invalid questionnaire expansion: Tried to exit an undefined answerPathContext');
        }
        this.answerPathContext.pop();
    }
    activeContext() {
        return this.answerPathContext[this.answerPathContext.length - 1] ?? undefined;
    }
    previousContext() {
        return this.answerPathContext[this.answerPathContext.length - 2] ?? undefined;
    }
    numberOfRepetitions(repeatableNode) {
        if (isRepeatableOptionsWithLimits(repeatableNode.options)) {
            switch (this.repetitionIntervalBoundary) {
                case RepetitionIntervalBoundary.minRepetitions:
                    return repeatableNode.options.minRepetitions;
                case RepetitionIntervalBoundary.maxRepetitions:
                    return repeatableNode.options.maxRepetitions;
            }
            return assertUnreachable(this.repetitionIntervalBoundary);
        }
        return 0;
    }
    repeatedInstanceIdentifiers() {
        return this.activeContext() ?? { byBlueprintId: {}, byNodeId: {} };
    }
}
